// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/dialogs/shared/CopyLinkSection.module.css';
import {Button} from '@app/features/ui/button/Button';
import {Input} from '@app/features/ui/components/form/Input';
import {useLingui} from '@lingui/react/macro';
import {type ComponentProps, type MouseEvent, type ReactNode, useCallback, useEffect, useRef, useState} from 'react';

interface CopyLinkSectionProps {
	label: ReactNode;
	value: string;
	placeholder?: string;
	onCopy?: () => Promise<boolean>;
	copyDisabled?: boolean;
	onInputClick?: (event: MouseEvent<HTMLInputElement>) => void;
	rightElement?: ReactNode;
	inputProps?: Partial<ComponentProps<typeof Input>>;
	children?: ReactNode;
}
export const CopyLinkSection = ({
	label,
	value,
	placeholder,
	onCopy,
	copyDisabled,
	onInputClick,
	rightElement,
	inputProps,
	children,
}: CopyLinkSectionProps) => {
	const {t} = useLingui();
	const [copied, setCopied] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const buttonLabel = copied ? t`Copied!` : t`Copy`;
	const handleCopy = useCallback(async () => {
		if (!onCopy) return;
		const success = await onCopy();
		if (!success) return;
		setCopied(true);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = setTimeout(() => {
			setCopied(false);
		}, 3000);
	}, [onCopy]);
	useEffect(() => {
		if (!value) {
			setCopied(false);
		}
	}, [value]);
	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);
	const defaultRightElement = onCopy && (
		<Button compact fitContent onClick={handleCopy} disabled={!value || copyDisabled}>
			{buttonLabel}
		</Button>
	);
	return (
		<div className={styles.linkFooter}>
			<p className={styles.linkSectionLabel}>{label}</p>
			<Input
				readOnly
				value={value}
				placeholder={placeholder}
				onClick={onInputClick}
				rightElement={rightElement ?? defaultRightElement}
				{...inputProps}
			/>
			{children}
		</div>
	);
};
